import React, { useState, useMemo } from "react";
import { Typography, Box, IconButton, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";

import Section from "../../../layout/Section";
import SubSection from "../../../layout/SubSection";
import InputFieldNumber from "../../../common/inputs/InputFieldNumber";
import Dropdown from "../../../common/inputs/Dropdown";
import EditableLabel from "../../../common/inputs/EditableLabel";
import LeaseTiersEditor from "./LeaseTiersEditor";
import { formatCurrency } from "../../../../utils/formatters";
import { fonts, colors } from "../../../../styles/theme";
import {
  Opex,
  OpexItem,
  OpexSection as OpexSectionType,
  OpexItemCreate,
  OpexItemUpdate,
  CostInputMode,
  LeaseTier,
  LeaseTierCreate,
  LeaseTierUpdate,
  OPEX_SECTION_LABELS,
  OPEX_SECTION_ORDER,
} from "../../../../types";

/**
 * Modus-Optionen für die Dropdowns
 */
const MODUS_OPTIONS: { value: CostInputMode; label: string }[] = [
  { value: "prozent", label: "Anteil vom Erlös" },
  { value: "steigend", label: "Jährliche Gebühr" },
  { value: "manuell", label: "Manuell" },
];

/**
 * Einzelne OPEX-Zeile mit Modus-Dropdown und Wert-Eingabe
 */
interface OpexRowProps {
  item: OpexItem;
  canEdit: boolean;
  onUpdate: (itemId: string, data: OpexItemUpdate) => void;
  onDelete: (itemId: string) => void;
  /** Wert aus Jahr 1 für Anzeige bei Modus "manuell" */
  jahr1Wert?: number;
}

const OpexRow: React.FC<OpexRowProps> = ({
  item,
  canEdit,
  onUpdate,
  onDelete,
  jahr1Wert,
}) => {
  // Pachtkosten sind immer auf Modus "prozent" fixiert (erkennbar an is_editable=false in pachtkosten section)
  const isPacht = item.section === "pachtkosten" && !item.is_editable;
  const modus = item.modus || "steigend";
  const basiswert = item.basiswert || 0;
  const prozentSatz = item.prozent_satz || 0;

  const handleLabelChange = (value: string) => {
    onUpdate(item.id, { bezeichnung: value });
  };

  const handleModusChange = (value: string) => {
    if (!isPacht) {
      onUpdate(item.id, { modus: value as CostInputMode });
    }
  };

  const handleBasiswertChange = (value: string) => {
    const num = value ? parseFloat(value) : 0;
    onUpdate(item.id, { basiswert: isNaN(num) ? 0 : num });
  };

  const handleProzentSatzChange = (value: string) => {
    const num = value ? parseFloat(value) : 0;
    onUpdate(item.id, { prozent_satz: isNaN(num) ? 0 : num });
  };

  const handleSteigerungChange = (value: string) => {
    const num = value ? parseFloat(value) : 2;
    onUpdate(item.id, { steigerung: isNaN(num) ? 2 : num });
  };

  // Bei Modus "manuell" keine Eingabe hier
  const showBasiswert = modus !== "manuell" && modus !== "prozent";
  // Bei Modus "prozent" zeigen wir links %-Eingabe und rechts den berechneten €-Wert
  const showProzent = modus === "prozent";
  // Bei Modus "steigend" zeigen wir ein Steigerungs-%-Eingabefeld
  const showSteigerung = modus === "steigend";

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        py: 0.5,
        ml: "-42px", // Bündig mit dem Aufklapp-Icon
      }}
    >
      {/* Löschen-Button - ganz links, unter dem Aufklapp-Icon */}
      <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
        {item.is_editable && canEdit ? (
          <Tooltip title="Position entfernen">
            <IconButton
              size="small"
              onClick={() => onDelete(item.id)}
              sx={{
                color: colors.icon.subtle,
                "&:hover": { color: "error.main" },
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : (
          // Platzhalter für nicht-editierbare Items, um Alignment zu erhalten
          <Box sx={{ width: 40 }} />
        )}
      </Box>
      {/* Editierbare Bezeichnung */}
      <EditableLabel
        value={item.bezeichnung}
        editable={item.is_editable && canEdit}
        minWidth={150}
        onSave={handleLabelChange}
      />
      {/* 1. Modus Dropdown (links) - Bei Pacht immer disabled */}
      <Tooltip title="Abrechnungsmodus" placement="top" arrow>
        <Box>
          <Dropdown
            value={modus}
            options={
              isPacht
                ? [{ value: "prozent", label: "Anteil vom Erlös" }]
                : MODUS_OPTIONS
            }
            onChange={handleModusChange}
            disabled={isPacht || !canEdit}
            width={160}
          />
        </Box>
      </Tooltip>
      {/* 2. Prozentwert (Mitte) - nur bei bestimmten Modi, sonst Platzhalter */}
      {showProzent ? (
        <Tooltip title="Anteil vom Erlös" placement="top" arrow>
          <Box sx={{ width: 100 }}>
            <InputFieldNumber
              label=""
              value={String(prozentSatz)}
              onChange={handleProzentSatzChange}
              readOnly={!canEdit}
              suffix="%"
            />
          </Box>
        </Tooltip>
      ) : showSteigerung ? (
        <Tooltip title="Jährliche Steigerung" placement="top" arrow>
          <Box sx={{ width: 100 }}>
            <InputFieldNumber
              label=""
              value={String(item.steigerung || 2)}
              onChange={handleSteigerungChange}
              readOnly={!canEdit}
              suffix="%"
            />
          </Box>
        </Tooltip>
      ) : (
        <Box sx={{ width: 100 }} />
      )}
      {/* 3. Kosten-Wert (rechts) */}
      {showBasiswert ? (
        <Tooltip title="Jährliche Kosten (Jahr 1)" placement="top" arrow>
          <Box sx={{ width: 150, display: "flex", justifyContent: "flex-end" }}>
            <InputFieldNumber
              label=""
              value={String(basiswert)}
              onChange={handleBasiswertChange}
              readOnly={!canEdit}
              suffix="€"
              allowDecimals={false}
            />
          </Box>
        </Tooltip>
      ) : showProzent ? (
        <Tooltip title="Berechnete Kosten (Jahr 1)" placement="top" arrow>
          <Box sx={{ width: 150, display: "flex", justifyContent: "flex-end" }}>
            <InputFieldNumber
              label=""
              value={String(Math.round(basiswert))}
              readOnly={true}
              suffix="€"
              allowDecimals={false}
            />
          </Box>
        </Tooltip>
      ) : (
        <Tooltip
          title="Kosten aus manueller Eingabe (Jahr 1)"
          placement="top"
          arrow
        >
          <Box sx={{ width: 150, display: "flex", justifyContent: "flex-end" }}>
            <InputFieldNumber
              label=""
              value={String(jahr1Wert ?? 0)}
              readOnly={true}
              suffix="€"
              allowDecimals={false}
            />
          </Box>
        </Tooltip>
      )}
    </Box>
  );
};

/**
 * OPEX Unterabschnitt mit + Button
 */
interface OpexSubSectionProps {
  section: OpexSectionType;
  items: OpexItem[];
  totalCost: number;
  canEdit: boolean;
  onUpdate: (itemId: string, data: OpexItemUpdate) => void;
  onDelete: (itemId: string) => void;
  onAdd: (data: OpexItemCreate) => void;
  /** Jahr-1-Kosten für Modus "manuell" */
  jahr1Kosten?: Record<string, number>;
  /** Projektlaufzeit in Jahren (für LeaseTiersEditor) */
  projektLaufzeit: number;
  onLeaseTierChange?: (tierId: string, data: LeaseTierUpdate) => void;
  onLeaseTierAdd?: (data: LeaseTierCreate) => void;
  onLeaseTierDelete?: (tierId: string) => void;
  customLabel?: string;
  onLabelChange?: (newLabel: string) => void;
}

const OpexSubSection: React.FC<OpexSubSectionProps> = ({
  section,
  items,
  totalCost,
  canEdit,
  onUpdate,
  onDelete,
  onAdd,
  jahr1Kosten,
  projektLaufzeit,
  onLeaseTierChange,
  onLeaseTierAdd,
  onLeaseTierDelete,
  customLabel,
  onLabelChange,
}) => {
  const isLocked = section === "pachtkosten";
  const displayLabel = customLabel ?? OPEX_SECTION_LABELS[section] ?? section;

  const handleAddItem = () => {
    onAdd({
      section,
      bezeichnung: "Neue Position",
      basiswert: 0,
      modus: "steigend",
      // Ohne Angabe setzt der Server die Standardsteigerung (Schema-Default) – keine zweite Kopie hier.
    });
  };

  // Finde das Pacht-Item (is_editable=false in pachtkosten section)
  const pachtItem =
    section === "pachtkosten"
      ? items.find((item) => !item.is_editable)
      : undefined;

  return (
    <SubSection
      title={displayLabel}
      totalCost={totalCost > 0 ? formatCurrency(totalCost) + " (Jahr 1)" : "–"}
    >
      {/* Spezielle Behandlung für Pachtkosten mit LeaseTiers */}
      {section === "pachtkosten" &&
        pachtItem &&
        onLeaseTierChange &&
        onLeaseTierAdd &&
        onLeaseTierDelete && (
          <LeaseTiersEditor
            leaseTiers={pachtItem.lease_tiers || []}
            projektLaufzeit={projektLaufzeit}
            canEdit={canEdit}
            onTierChange={onLeaseTierChange}
            onTierAdd={onLeaseTierAdd}
            onTierDelete={onLeaseTierDelete}
            basiswert={pachtItem.basiswert ?? 0}
          />
        )}

      {/* Normale Items (außer das fixe Pacht-Item wenn LeaseTiers verwendet wird) */}
      {items
        .filter(
          (item) =>
            !(
              section === "pachtkosten" &&
              !item.is_editable &&
              onLeaseTierChange
            ),
        )
        .map((item) => (
          <OpexRow
            key={item.id}
            item={item}
            canEdit={canEdit}
            onUpdate={onUpdate}
            onDelete={onDelete}
            jahr1Wert={jahr1Kosten?.[item.id]}
          />
        ))}

      {/* Hinzufügen-Button - bündig mit den Mülleimer-Icons (nicht bei Pachtkosten) */}
      {canEdit && section !== "pachtkosten" && (
        <Box sx={{ mt: 1, ml: "-42px", display: "flex" }}>
          <Box sx={{ width: 40, display: "flex", justifyContent: "center" }}>
            <Tooltip title="Position anlegen">
              <IconButton
                size="small"
                onClick={handleAddItem}
                sx={{
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: "primary.light",
                    color: "primary.contrastText",
                  },
                }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      )}
    </SubSection>
  );
};

interface OpexSectionProps {
  opex: Opex | null;
  canEdit: boolean;
  onItemUpdate: (itemId: string, data: OpexItemUpdate) => void;
  onItemDelete: (itemId: string) => void;
  onItemCreate: (data: OpexItemCreate) => void;
  /** Jahr-1-Kosten für Modus "manuell" */
  jahr1Kosten?: Record<string, number>;
  /** Projektlaufzeit in Jahren (für LeaseTiersEditor) */
  projektLaufzeit: number;
  /** Callback wenn eine Pachtkostenstaffel geändert wird */
  onLeaseTierChange?: (tierId: string, data: LeaseTierUpdate) => void;
  /** Callback wenn eine neue Pachtkostenstaffel hinzugefügt wird */
  onLeaseTierAdd?: (data: LeaseTierCreate) => void;
  /** Callback wenn eine Pachtkostenstaffel gelöscht wird */
  onLeaseTierDelete?: (tierId: string) => void;
  onSectionLabelChange?: (sectionKey: string, label: string) => void;
}

/**
 * OPEX Section - Betriebskosten pro Jahr
 * Item-basiertes Modell mit editierbaren Bezeichnungen
 */
const OpexSection: React.FC<OpexSectionProps> = ({
  opex,
  canEdit,
  onItemUpdate,
  onItemDelete,
  onItemCreate,
  jahr1Kosten,
  projektLaufzeit,
  onLeaseTierChange,
  onLeaseTierAdd,
  onLeaseTierDelete,
  onSectionLabelChange,
}) => {
  // Items nach Section gruppieren
  const itemsBySection = useMemo(() => {
    const map: Record<OpexSectionType, OpexItem[]> = {
      pachtkosten: [],
      wartung: [],
      versicherungen: [],
      banken: [],
      sonstige_abgaben: [],
    };

    if (opex?.items) {
      for (const item of opex.items) {
        if (map[item.section]) {
          map[item.section].push(item);
        }
      }

      // Sortieren nach sort_order
      for (const section of Object.keys(map) as OpexSectionType[]) {
        map[section].sort((a, b) => a.sort_order - b.sort_order);
      }
    }

    return map;
  }, [opex?.items]);

  // Summen pro Section berechnen (Jahr 1)
  const sectionSums = useMemo(() => {
    const sums: Record<OpexSectionType, number> = {
      pachtkosten: 0,
      wartung: 0,
      versicherungen: 0,
      banken: 0,
      sonstige_abgaben: 0,
    };

    for (const section of OPEX_SECTION_ORDER) {
      sums[section] = itemsBySection[section].reduce((sum, item) => {
        // Bei "manuell": Jahr-1-Wert verwenden
        if (item.modus === "manuell") {
          return sum + (jahr1Kosten?.[item.id] || 0);
        }
        // Bei allen anderen Modi: Basiswert verwenden
        return sum + (item.basiswert || 0);
      }, 0);
    }

    return sums;
  }, [itemsBySection, jahr1Kosten]);

  // Gesamtsumme
  const summeGesamt = useMemo(() => {
    return OPEX_SECTION_ORDER.reduce(
      (sum, section) => sum + sectionSums[section],
      0,
    );
  }, [sectionSums]);

  return (
    <Section
      title="Betriebskosten"
      actions={
        <Typography
          sx={{
            fontWeight: fonts.weights.semiBold,
            fontSize: "1rem",
            color: colors.accent.teal,
            fontFamily: fonts.monospace,
          }}
        >
          {formatCurrency(summeGesamt)} (Jahr 1)
        </Typography>
      }
    >
      {OPEX_SECTION_ORDER.map((section) => (
        <OpexSubSection
          key={section}
          section={section}
          items={itemsBySection[section]}
          totalCost={sectionSums[section]}
          canEdit={canEdit}
          onUpdate={onItemUpdate}
          onDelete={onItemDelete}
          onAdd={onItemCreate}
          jahr1Kosten={jahr1Kosten}
          projektLaufzeit={projektLaufzeit}
          onLeaseTierChange={
            section === "pachtkosten" ? onLeaseTierChange : undefined
          }
          onLeaseTierAdd={
            section === "pachtkosten" ? onLeaseTierAdd : undefined
          }
          onLeaseTierDelete={
            section === "pachtkosten" ? onLeaseTierDelete : undefined
          }
          customLabel={opex?.section_labels?.[section]}
          onLabelChange={
            onSectionLabelChange
              ? (label) => onSectionLabelChange(section, label)
              : undefined
          }
        />
      ))}
    </Section>
  );
};

export default React.memo(OpexSection);
